/**
 * THE PRODUCT'S GLYPHS. One drawing per meaning, and this file is where the
 * meaning is decided.
 *
 * It replaced three sets that had grown independently — the table chrome's, the
 * navigation's and the laboratory's. Two of them held byte-identical copies of
 * the funnel and of the month grid; two held DIFFERENT ticks, both meaning
 * "this one is chosen", on screens a clinician moves between; three held a
 * chevron with three different resting angles. Nothing was wrong in any one
 * file, which is the point: a second set is never added by someone who thinks
 * the product should have two, it is added by someone who did not know about
 * the first.
 *
 * A GLYPH IS NAMED AFTER WHAT IT DEPICTS, never after where it leads or what it
 * saves. A drawing serves whichever surface wants it — the shield stands for
 * roles and access and would stand for any other guarded thing — and a set
 * named by destination grows a second identical drawing the first time two
 * screens want one picture.
 *
 * AND WHAT IT DEPICTS IS WHAT THE READER SEES. The shield used to stand for
 * the patient registry because a registry is a guarded list; a reader of the
 * rail, where the words are folded away, saw a shield and read "security".
 * Under the same rule the branch stood for a catalogue of costs because the
 * catalogue is a tree, and three sections of one column each wore the same
 * calendar, or the same sheet, because each was nearly one. The rail is where
 * that fails: two tiles with one picture are indistinguishable there, and a
 * picture chosen for what the destination IS rather than what it looks like
 * is a riddle. So a navigation tile's glyph is the picture a stranger would
 * draw for its NAME, and no two tiles of one column share one. The map from a product ACTION to a glyph
 * lives in `RowAction.tsx`, which is a different question with a different
 * owner.
 *
 * `currentColor`, no fill: a glyph is text as far as the theme is concerned, so
 * it inherits the ink of whatever it sits in and needs no per-theme value in
 * any theme or either mode. Size and stroke weight come from `--icon-size-*`
 * and `--icon-stroke`, which ARE part of the theme contract.
 *
 * ALL GEOMETRY IS DRAWN IN A 24-UNIT SQUARE. A glyph authored at another scale
 * cannot be mixed with these at one size, which is how the sign-out arrow ended
 * up as a fourth set of one.
 *
 * MIRRORING IS A PROPERTY OF THE DRAWING, declared here rather than decided by
 * each surface. Most of these are pictures of things and must NOT flip: a
 * printer, a shield and a paper plane read the same in Hebrew. A few are
 * spatial statements — an arrow leaving, a chevron pointing onward, a pane
 * docked at one edge of the shell — and those are listed in `DIRECTIONAL`,
 * which is what `Icon` turns into the one flip rule in `styles/icon.css`. The
 * journal's outbound arrow used to be flipped by the journal's own stylesheet,
 * which meant the next surface to render one had to know to copy that rule.
 */

export const ICON_PATHS = {
  ribbon: [
    'M12 11.22C11 9.997 10 9 10 8a2 2 0 0 1 4 0c0 1-.998 2.002-2.01 3.22',
    'm12 18 2.57-3.5', 'M6.243 9.016a7 7 0 0 1 11.507-.009', 'M9.35 14.53 12 11.22',
    'M9.35 14.53C7.728 12.246 6 10.221 6 7a6 5 0 0 1 12 0c-.005 3.22-1.778 5.235-3.43 7.5l3.557 4.527a1 1 0 0 1-.203 1.43l-1.894 1.36a1 1 0 0 1-1.384-.215L12 18l-2.679 3.593a1 1 0 0 1-1.39.213l-1.865-1.353a1 1 0 0 1-.203-1.422z',
  ],
  /* Table, filter and menu chrome ---------------------------------------- */

  /** Column filter, the funnel of every spreadsheet. */
  filter: ['M3 4h18l-7 8v6l-4 2v-8z'],
  /** The funnel with a cross where its far corner was: every narrowing taken
   *  off at once, the toolbar's undo-everything drawn as its glyph. */
  'filter-x': ['M13 4H3l7 8v8l4-2v-6l1.3-1.5', 'm16.5 3.5 4.5 4.5', 'm21 3.5-4.5 4.5'],
  /** Take a column off the table, or fold a window away. Its arrow pushes the
   *  middle line toward the end of the reading direction, so it mirrors. */
  fold: ['M3 5h18', 'M3 12h12', 'M3 19h18', 'm17 9 3 3-3 3'],
  /** Take the floating window somewhere else: the four-way move cross. */
  move: ['M12 2v20', 'M2 12h20', 'm9 5 3-3 3 3', 'm9 19 3 3 3-3', 'm5 9-3 3 3 3', 'm19 15 3-3-3-3'],
  /** Row expander. Points down closed, and is rotated when open. */
  'chevron-down': ['m6 9 6 6 6-6'],
  /** The way back up a list: the step a row takes toward the front of an
   *  order. Not directional — a list runs down the page in every locale. */
  'chevron-up': ['m6 15 6-6 6 6'],
  /** Onward: the next level of a tree, the tile's destination, a closed menu. */
  'chevron-right': ['m9 6 6 6-6 6'],
  /** Backward: the previous page of whatever the onward chevron advances. */
  'chevron-left': ['m15 6-6 6 6 6'],
  /** Fold toward the reading start: the navigation panel giving its width
   *  back to the work. Two chevrons, because one is a step and two are a
   *  whole side of the screen. */
  'chevrons-left': ['m11 17-5-5 5-5', 'm18 17-5-5 5-5'],
  /** Unfold toward the reading end: the rail bringing the whole panel back. */
  'chevrons-right': ['m6 17 5-5-5-5', 'm13 17 5-5-5-5'],
  /** Clear a filter, dismiss a chip, abandon an edit. */
  close: ['M18 6 6 18', 'm6 6 12 12'],
  /**
   * Confirm, commit, complete, chosen. The check, not a floppy disk: the disk
   * names a device nobody has, the check names the outcome. ONE tick — the
   * laboratory drew a second one and the two sat two clicks apart.
   */
  check: ['M20 6 9 17l-5-5'],
  /** The leading mark of a worded "add" action. Two strokes, no glyph font:
   *  a literal "+" in the label would be punctuation a screen reader reads. */
  plus: ['M12 5v14', 'M5 12h14'],
  /** One fewer. The plus without its upright, so the pair reads as a pair on
   *  either side of the number they step. */
  minus: ['M5 12h14'],
  /** Copy a value to the clipboard. */
  copy: ['M8 8h11a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H10a2 2 0 0 1-2-2Z', 'M16 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h3'],
  /** Cut: take the thing to the clipboard and leave nothing behind. */
  scissors: [
    'M6 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z',
    'M6 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z',
    'M20 4 8.12 15.88',
    'm14.47 14.48 5.53 5.52',
    'm8.12 8.12 3.88 3.88',
  ],
  /** Paste: the board the copied thing is waiting on. */
  clipboard: [
    'M9 2h6a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1Z',
    'M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2',
  ],
  /** A row's own menu of actions. Round line caps turn the degenerate strokes
   *  into three dots — the vertical kebab. */
  dots: ['M12 5v.01', 'M12 12v.01', 'M12 19v.01'],
  /**
   * The world: equator and one meridian inside the set's own circle, the same
   * arc `ban` and `clock` are drawn from, so the three sit at one radius.
   *
   * It stands for the INTERFACE's language, which is why it is not `languages`.
   * That glyph is letterforms and means turning a written record from one
   * language into another; a clinician who reaches for this one is not
   * translating anything, they are saying which language the product should
   * speak to them in. Two meanings, so two drawings — and a picture of the
   * world says the second without depending on a script the reader may be
   * looking for precisely because they cannot read the current one.
   */
  globe: [
    'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z',
    'M3 12h18',
    'M12 3a4.5 9 0 0 0 0 18 4.5 9 0 0 0 0-18',
  ],
  /**
   * Something described in words becoming something the product did: the wand
   * with its sparks. It marks the AI affordances, and it is deliberately NOT
   * the funnel — a second funnel in the header row would read as one more
   * column filter rather than as the control that sets all of them.
   */
  sparkle: [
    'M4 20 14 10',
    'm17 3 .9 2.1L20 6l-2.1.9L17 9l-.9-2.1L14 6l2.1-.9z',
    'm7 4 .6 1.4L9 6l-1.4.6L7 8l-.6-1.4L5 6l1.4-.6z',
    'm19 14 .6 1.4L21 16l-1.4.6L19 18l-.6-1.4L17 16l1.4-.6z',
  ],
  /**
   * A TICK WITH A SPARK: something the AI chose that a person has since
   * confirmed. The tick is the confirmation and the spark says where the value
   * came from, which is the order the fact is read in — a person's agreement,
   * about an AI value.
   *
   * IT IS NOT THE WAND WITH A TICK ADDED, AND THAT WAS THE DEFECT. This used
   * to keep the wand's line and two of its sparks and replace only the third
   * with a small check, so the two marks had the same silhouette — a diagonal
   * with things scattered along it — and at the 14px a chip wears them at,
   * "chosen by AI" and "confirmed by a person" were one picture. A mark whose
   * whole job is to separate two states has to be separable at the size it is
   * actually drawn, so the tick takes the glyph and the spark rides its open
   * corner: one stroke the eye reads first, not a fourth detail among three.
   */
  'sparkle-check': [
    'M20 9 11.5 17.5l-4-4',
    'm6 3 .9 2.1L9 6l-2.1.9L6 9l-.9-2.1L3 6l2.1-.9z',
  ],
  /**
   * A circle with a question in it: a fact the record does not carry. The
   * provenance mark wears it for a value whose origin was never recorded, and
   * says so in words — it is deliberately not the person, because a missing
   * origin presented as a person's choice would be a fabricated confirmation.
   */
  question: [
    'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Z',
    'M9.5 9.5a2.5 2.5 0 1 1 3.6 2.2c-.7.4-1.1 1-1.1 1.8',
    'M12 17h.01',
  ],
  /**
   * Lines of text set off by a quotation bar: a prompt, written down to be
   * given to a model. The AI area's door wears the wand, so its registry of
   * prompts needs a picture of its own.
   */
  'text-quote': ['M17 6H3', 'M21 12H8', 'M21 18H8', 'M3 12v6'],

  /* Acting on one record ------------------------------------------------- */

  /** Rename, edit in place, open the editor for this row. */
  pencil: [
    'M21.17 6.81a2.82 2.82 0 0 0-3.99-3.98L3.84 16.17a2 2 0 0 0-.5.83l-1.32 4.35a.5.5 0 0 0 .62.63l4.36-1.32a2 2 0 0 0 .83-.5z',
    'm15 5 4 4',
  ],
  /** Delete. The lidded bin, without motion lines: the action is already loud. */
  trash: [
    'M3 6h18',
    'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6',
    'M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2',
  ],
  /**
   * Withdraw, cancel, refuse — a thing that will not now happen. The barred
   * circle rather than the cross: the cross already means "dismiss what is on
   * screen", and a row whose cancel and whose close wear one glyph is a row
   * where the destructive one is reached by accident.
   */
  ban: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z', 'm5.6 5.6 12.8 12.8'],
  /** Leave for the record's own workspace: the outbound arrow. */
  'arrow-out': ['M7 7h10v10', 'M7 17 17 7'],
  /** The way back: an arrow toward the reading start, where the way-back link
   *  stands when the navigation rail has room for a glyph and no room for
   *  its words. */
  'arrow-left': ['m12 19-7-7 7-7', 'M19 12H5'],
  /** Leave the product entirely: a door with the arrow going through it. */
  exit: ['M14 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8', 'M17 8.5 20.5 12 17 15.5', 'M9.5 12h11'],
  /** Open this row in the workspace's CONTEXT panel: the pane docked at the
   *  trailing edge, beside the table that stays on screen. */
  'panel-end': ['M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z', 'M16 5v14'],
  /** The NAVIGATION panel, at the shell's leading edge — what the collapsed
   *  rail brings back. A different panel from the one above, on the other side
   *  of the workspace, so a different drawing. */
  'panel-start': ['M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z', 'M9 5v14'],
  download: ['M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4', 'm7 10 5 5 5-5', 'M12 15V3'],
  /** Upload: the download arrow, reversed. */
  upload: ['M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4', 'm7 8 5-5 5 5', 'M12 3v12'],
  /** The shortlist star. Stroke when off; the pressed state fills it in CSS. */
  star: [
    'M11.53 2.3a.53.53 0 0 1 .94 0l2.32 4.68a2.12 2.12 0 0 0 1.6 1.16l5.16.75a.53.53 0 0 1 .3.91l-3.74 3.64a2.12 2.12 0 0 0-.61 1.88l.88 5.14a.53.53 0 0 1-.77.56l-4.62-2.43a2.12 2.12 0 0 0-1.97 0l-4.62 2.43a.53.53 0 0 1-.77-.56l.88-5.14a2.12 2.12 0 0 0-.61-1.88L2.16 9.8a.53.53 0 0 1 .3-.91l5.16-.75a2.12 2.12 0 0 0 1.6-1.16z',
  ],
  /** The VIP crown: the CLINIC's classification of a Person. A different
   *  drawing from the star above on purpose — the star is one practitioner's
   *  own shortlist, the crown is a fact of the record — and symmetric, so it
   *  reads the same in Hebrew. */
  crown: [
    'M11.56 3.27a.5.5 0 0 1 .88 0l2.95 5.6a1 1 0 0 0 1.52.3l4.27-3.67a.5.5 0 0 1 .8.52l-2.83 10.25a1 1 0 0 1-.96.73H5.81a1 1 0 0 1-.96-.73L2.02 6.02a.5.5 0 0 1 .8-.52l4.27 3.67a1 1 0 0 0 1.52-.3z',
    'M5 21h14',
  ],
  /** Put back what a filter, a selection or an edit took away. */
  restore: ['M3 12a9 9 0 1 0 3-6.7L3 8', 'M3 3v5h5'],
  /**
   * TURN A PAGE BY A QUARTER, either way round: the restore arc — one drawing
   * for "round again" — with a page in its centre, so it reads as the page
   * being turned rather than the record being redone. The arc runs the way
   * the page goes, and the pair are pictures of a turn, not of a direction
   * along the line, so neither mirrors.
   */
  'rotate-left': ['M3 12a9 9 0 1 0 3-6.7L3 8', 'M3 3v5h5', 'M9 9h6v6H9Z'],
  'rotate-right': ['M21 12a9 9 0 1 1-3-6.7L21 8', 'M21 3v5h-5', 'M9 9h6v6H9Z'],
  /**
   * A painter's palette with its thumb hole and four dabs of paint: design
   * work, drafted rather than built.
   */
  palette: [
    'M12 22a10 10 0 1 1 10-10c0 2.5-2 4-4.5 4h-2a1.75 1.75 0 0 0-1.3 2.9 1.75 1.75 0 0 1-1.3 3.1Z',
    'M13.5 6.5h.01', 'M17.5 10.5h.01', 'M8.5 7.5h.01', 'M6.5 12.5h.01',
  ],
  /** Two corner marks crossing: the frame a crop is cut along. */
  crop: ['M6 2v14a2 2 0 0 0 2 2h14', 'M18 22V8a2 2 0 0 0-2-2H2'],
  /**
   * Four corners pushed outward: what stands in this frame, opened at the size
   * of the screen. It is not the eye — the eye shows something hidden, and the
   * page this enlarges is already in view.
   */
  expand: [
    'M8 3H5a2 2 0 0 0-2 2v3',
    'M21 8V5a2 2 0 0 0-2-2h-3',
    'M3 16v3a2 2 0 0 0 2 2h3',
    'M16 21h3a2 2 0 0 0 2-2v-3',
  ],
  /**
   * The restoring arrow with the clock's hands inside it: what was opened a
   * moment ago. The recently-opened tray wore the bare clock, which reads as
   * time rather than as the past and stood on the rail directly above the
   * calendar, so the two time-shaped glyphs had to be told apart by their
   * words.
   */
  history: ['M3 12a9 9 0 1 0 3-6.7L3 8', 'M3 3v5h5', 'M12 7.5V12l3 2'],
  /** Look at stored content without changing it. */
  eye: ['M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z', 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z'],
  'eye-off': ['M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z', 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z', 'M3 3l18 18'],

  /* Places, sections and destinations ------------------------------------ */

  /** A shield with a check: the register of people this clinic is trusted with. */
  'shield-check': ['M12 3 20 7v5c0 4.4-3.2 7.6-8 9-4.8-1.4-8-4.6-8-9V7Z', 'm9 12 2 2 4-4'],
  /** A month grid: the day this clinician works through, and any date field. */
  calendar: ['M8 2v4', 'M16 2v4', 'M3 9h18', 'M5 5h14a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z'],
  /**
   * The grid with a clock over its corner: the HOURS a clinic keeps on each
   * day, which is what the working calendar holds. The schedule area used to
   * draw three plain calendars in a row — the calendar, the working calendar
   * and availability — and on the rail they were one tile three times.
   */
  'calendar-clock': [
    'M8 2v4',
    'M16 2v4',
    'M3 9h6',
    'M20 8V6a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h4',
    'M16 22a6 6 0 1 0 0-12 6 6 0 0 0 0 12Z',
    'M16 13.5V16l2 1.2',
  ],
  /** The grid with a tick in it: the days someone is there to be booked. */
  'calendar-check': [
    'M8 2v4',
    'M16 2v4',
    'M3 9h18',
    'M5 5h14a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z',
    'm9 15.5 2 2 4-4',
  ],
  /**
   * A gear: settings for the thing it stands on — the clinic's own, rather
   * than a patient's chart, and in a table's header corner the columns that
   * table shows. Three standing bars used to draw the second of those; they
   * named the object where the gear names the act, and beside the funnel they
   * read as a second grid rather than as a control.
   */
  gear: [
    'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z',
    'M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-2.7 1.1V21a2 2 0 0 1-4 0v-.1a1.6 1.6 0 0 0-2.6-1.1l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1A1.6 1.6 0 0 0 4 13.6a2 2 0 0 1 0-4 1.6 1.6 0 0 0 1.4-2.7l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1A1.6 1.6 0 0 0 11 4a2 2 0 0 1 4 0 1.6 1.6 0 0 0 2.7 1.1l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8Z',
  ],
  /** A person: whose chart this is. */
  person: ['M20 21a8 8 0 1 0-16 0', 'M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z'],
  /**
   * A person with a plus beside them: someone the clinic has not registered
   * yet — a lead, and the onboarding that turns one into a patient. The bare
   * person wore this tile before, and a bare person is a profile: it stood on
   * the rail two tiles under the patient registry and said nothing to tell
   * "new enquiries" from "who is signed in".
   */
  'user-plus': ['M2 21a8 8 0 0 1 13.3-6', 'M10 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z', 'M19 16v6', 'M22 19h-6'],
  /**
   * The female sign, and the male sign under it. Drawn rather than typed:
   * these two used to be the characters ♀ and ♂, whose hairline strokes are
   * the font's and thin out of existence at reduced zoom — the one place a
   * vanished glyph is a clinical fact gone missing. As registry drawings they
   * carry the set's own stroke at every scale.
   */
  female: ['M12 13a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9Z', 'M12 13v8', 'M8.5 17.5h7'],
  male: ['M10 18.5a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9Z', 'M13.2 10.8 20 4', 'M14.8 4H20v5.2'],
  /** A stethoscope: a clinician in a directory of possible recipients. */
  stethoscope: [
    'M6 3v5a4 4 0 0 0 8 0V3',
    'M4 3h4',
    'M12 3h4',
    'M10 12v2a5 5 0 0 0 10 0v-1',
    'M20 13a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z',
  ],
  /** An identity card: the administrative facts the title sheet holds. */
  'identity-card': [
    'M3 5h18a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z',
    'M8 11a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z',
    'M6 16c0-1.7 1.3-3 4-3s4 1.3 4 3',
    'M15 9h4',
    'M15 13h4',
  ],
  /**
   * A case with a cross on it: an episode of care, the CASE a chart's work is
   * filed under. The section wore the price tag, chosen as "a named grouping",
   * and the tag is what the Price list wears on the rail one column over.
   */
  'briefcase-medical': [
    'M4 6h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z',
    'M16 6V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2',
    'M6 6v14',
    'M18 6v14',
    'M12 11v4',
    'M10 13h4',
  ],
  /**
   * A registered building, its annex beside it: one legal identity in the
   * clinic's register of them. The hospital below is the clinic itself; this
   * is the picture of an entity a clinic contracts, invoices and is paid as.
   * A thing rather than a spatial statement, so it does not mirror.
   */
  building: [
    'M3 21V5a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v16',
    'M14 21V9h5a2 2 0 0 1 2 2v10',
    'M2 21h20',
    'M6 8h2',
    'M10 8h2',
    'M6 12h2',
    'M10 12h2',
    'M7 21v-4h3v4',
  ],
  /**
   * A building with a cross over its door: the clinic itself, as an
   * organization. It wore the identity card, which is the patient's title
   * sheet one area over; the card is a person's administrative facts, and the
   * clinic is a place.
   */
  hospital: [
    'M12 6v4',
    'M10 8h4',
    'M10 14h4',
    'M10 18h4',
    'M18 12h2a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2h2',
    'M18 22V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v18',
  ],
  /** A map pin: a place — a room, a site, an organization something is booked at. */
  'map-pin': [
    'M12 22c-1.9-1.6-8-7-8-12a8 8 0 0 1 16 0c0 5-6.1 10.4-8 12Z',
    'M12 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z',
  ],
  /** A written sheet: what was recorded during care. */
  document: ['M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z', 'M14 3v5h5', 'M9 13h6', 'M9 17h4'],
  /**
   * An open book: a dictionary, which is what a terminology is. Three tiles of
   * the Control Center — terminology, print forms and protocols — used to wear
   * the written sheet, and a column of three sheets is a column read by its
   * words.
   */
  'book-open': [
    'M12 7v14',
    'M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3Z',
  ],
  /** A scroll with writing on it: a rule set down to be followed — a protocol. */
  'scroll-text': [
    'M15 12h-5',
    'M15 8h-5',
    'M19 17V5a2 2 0 0 0-2-2H4',
    'M8 21h12a2 2 0 0 0 2-2v-1a1 1 0 0 0-1-1H11a1 1 0 0 0-1 1v1a2 2 0 1 1-4 0V5a2 2 0 1 0-4 0v2a1 1 0 0 0 1 1h3',
  ],
  /** A printer with a sheet in it: paper coming out of the machine. */
  printer: [
    'M7 9V4a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v5',
    'M7 18H5a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2',
    'M8 14h8a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1v-5a1 1 0 0 1 1-1Z',
  ],
  /** A receipt: issued charges gathered into one financial document. */
  receipt: [
    'M4 2v20l2-2 2 2 2-2 2 2 2-2 2 2 2-2 2 2V2l-2 2-2-2-2 2-2-2-2 2-2-2-2 2Z',
    'M16 8h-6',
    'M16 12h-6',
    'M13 16h-3',
  ],
  /**
   * Two coins: money the clinic pays out, which is what an external cost is.
   * The catalogue wore the branch point because it is laid out as a tree, and
   * a branch point on the rail reads as a fork in a version history. Beside
   * the price tag — what the clinic charges — the coins are the other half of
   * one pair.
   */
  coins: [
    'M8 14a6 6 0 1 0 0-12 6 6 0 0 0 0 12Z',
    'M18.1 10.4A6 6 0 1 1 10.3 18',
    'M7 6h1v4',
    'm16.7 13.9.7.7-2.8 2.8',
  ],
  /** A bank card for account and receipt surfaces. */
  'credit-card': [
    'M3 5h18a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z',
    'M2 10h20',
    'M6 15h4',
  ],
  /** A cash register for the Finance workspace entry point. */
  'cash-register': [
    'M5 8h14a1 1 0 0 1 1 1v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V9a1 1 0 0 1 1-1Z',
    'M8 3h8v5H8Z',
    'M8 12h.01',
    'M12 12h.01',
    'M16 12h.01',
    'M7 17h10',
  ],
  /** A paper plane: something sent onward from here. */
  'paper-plane': ['M22 2 11 13', 'M22 2 15 22l-4-9-9-4Z'],
  /**
   * A plug: a connection to something outside — the mail, Telegram and
   * WhatsApp connectors as one tile. The paper plane it wore is the act of
   * sending, and very nearly one messenger's own mark, on a tile that stands
   * for three channels and their credentials.
   */
  plug: ['M12 22v-5', 'M9 8V2', 'M15 8V2', 'M18 8v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8Z'],
  /** A folder: everything received or produced alongside the record, and any
   *  named group of saved things. */
  folder: ['M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z'],
  /** The same folder with a plus on it: a new folder, made where the reader stands. */
  'folder-plus': ['M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z', 'M12 10.5v5', 'M9.5 13h5'],
  /** Two folders, one behind the other: every folder below this one, not
   *  this one alone. */
  folders: ['M8 8a2 2 0 0 1 2-2h3l2 2h5a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H10a2 2 0 0 1-2-2z', 'M4 14V6a2 2 0 0 1 2-2h3l1.5 1.5'],
  /** A trunk with two folders hanging off it: folders shown as the tree they
   *  are. Mirrors, because the trunk stands at the start of the line and the
   *  folders hang toward its end, as an indented tree does. */
  'folder-tree': [
    'M13 3h2.5l1.5 1.5H20a1 1 0 0 1 1 1V9a1 1 0 0 1-1 1h-7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z',
    'M13 14h2.5l1.5 1.5H20a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1h-7a1 1 0 0 1-1-1v-5a1 1 0 0 1 1-1z',
    'M4 2v15a2 2 0 0 0 2 2h6',
    'M4 6.5h8',
  ],
  /** A house: the top of a hierarchy the reader can always go back to — the
   *  root of a chart's folders, above every one of them. */
  home: ['m3 11 9-7 9 7', 'M5.5 9.5V20h4.5v-5.5h4V20h4.5V9.5'],
  /** An archive box: a record retained for reading but no longer active. */
  archive: ['M21 8v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8', 'M1 3h22v5H1Z', 'M10 12h4'],
  /** Two identities converging into one surviving record. */
  merge: [
    'M6 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z',
    'M18 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z',
    'M6 9v3a6 6 0 0 0 6 6h3',
  ],
  /**
   * A price tag on a ring: the price list, and a named, optionally coloured
   * grouping something is filed under. The Case section wore it for the second
   * sense and lost it to the first: on the rail the tag is the Price list.
   */
  tag: ['M4 12.5V5a1 1 0 0 1 1-1h7.5a1 1 0 0 1 .7.3l7 7a1 1 0 0 1 0 1.4l-7.5 7.5a1 1 0 0 1-1.4 0l-7-7a1 1 0 0 1-.3-.7Z', 'M8.5 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z'],
  /** A pushpin: whether the panel is fastened open or lets go and collapses. */
  pin: ['M12 17v5', 'M9 3h6l-1 7 4 3H6l4-3-1-7Z'],

  /* Clinical instruments -------------------------------------------------- */

  /** Translation between written languages. */
  languages: ['m5 8 6 6', 'm4 14 6-6 2-3', 'M2 5h12', 'M7 2h1', 'm22 22-5-10-5 10', 'M14 18h6'],
  microphone: [
    'M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z',
    'M5 10v2a7 7 0 0 0 14 0v-2',
    'M12 19v3',
  ],
  /**
   * A flask: the laboratory as a PLACE, which is what a destination tile
   * names. The two views inside it are `activity` and `table`, and the report
   * documents beside them are `folder` — a tile is told apart from its
   * neighbours by its glyph, so the laboratory borrowing the checked shield
   * meant one clinical destination read as verification.
   */
  flask: [
    'M9 3h6',
    'M10 3v6.6L4.8 19.4A1.5 1.5 0 0 0 6.1 21.7h11.8a1.5 1.5 0 0 0 1.3-2.3L14 9.6V3',
    'M7 16h10',
  ],
  /** A cross-sectional medical image inside its acquisition frame. */
  scan: [
    'M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z',
    'M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10Z',
    'M12 9v6',
    'M9 12h6',
  ],
  /** A trace over time: a measurement read as a series rather than a value. */
  activity: ['M3 12h4l2 6 4-14 2 8h6'],
  /** A branch point: one series splitting into the events beside it. */
  branch: [
    'M6 3v12',
    'M18 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z',
    'M6 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z',
    'M15 6a9 9 0 0 1-9 9',
  ],
  search: ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16Z', 'm21 21-4.3-4.3'],
  /** Faders: several settings adjusted together. */
  sliders: [
    'M4 21v-7',
    'M4 10V3',
    'M12 21v-9',
    'M12 8V3',
    'M20 21v-5',
    'M20 12V3',
    'M1 14h6',
    'M9 8h6',
    'M17 16h6',
  ],
  /** A grid of rows and columns: the results read as a matrix. */
  table: [
    'M6 5h12a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z',
    'M4 10h16',
    'M9 5v14',
  ],
  /** A value outside its reference range, or a read that did not answer. */
  warning: ['M12 3 22 20H2z', 'M12 9v5', 'M12 17h.01'],
  /** A bug: the application behaviour that needs attention. */
  bug: [
    'M8 7V6a4 4 0 0 1 8 0v1',
    'M7 9h10v5a5 5 0 0 1-10 0z',
    'M12 9v10',
    'M4 13h3',
    'M17 13h3',
    'M5 7l2 2',
    'm19 7-2 2',
    'M6 18l2-2',
    'm18 18-2-2',
  ],
  /**
   * A conventional horizontal key: authority a person HOLDS for a while, which
   * is what developer elevation is — raised with a reason, ended by hand or by
   * its own clock. The level shaft leaves either top corner clear for a
   * surface-owned status mark instead of turning the two drawings into one
   * diagonal symbol. Deliberately not the checked shield the navigation wears:
   * that one stands for a guarded LIST, and this is the thing that opens one.
   */
  key: [
    'M3.5 12a4.5 4.5 0 1 0 9 0 4.5 4.5 0 0 0-9 0Z',
    'M12.5 12H21',
    'M18 12v3',
    'M15.5 12v2',
  ],
  /* The day's vocabulary ---------------------------------------------------
     What the dashboard board points at: a thing demanding a reply now, a list
     of work, a clock, and the three ways a clinician reaches a person. */

  /** A bell: something is waiting for a decision and will not wait quietly. */
  bell: ['M12 3a5 5 0 0 0-5 5v3.4L5.4 15h13.2L17 11.4V8a5 5 0 0 0-5-5Z', 'M10 18a2 2 0 0 0 4 0'],
  /** A board with a tick: work assigned and worked through, item by item. */
  'clipboard-check': [
    'M9 4H7a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-2',
    'M9 2h6v4H9z',
    'm9.5 13.5 2 2 4-4.5',
  ],
  /**
   * Two people: a process held OPEN between the clinic and someone else, which
   * is what a follow-up is, and the registry of patients, which is many of
   * them. `person` stays the single subject of a record.
   */
  people: [
    'M9.5 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z',
    'M3 20a6.5 6.5 0 0 1 13 0',
    'M16.5 4.6a3.5 3.5 0 0 1 0 6.8',
    'M17.5 14.4A5.5 5.5 0 0 1 21 19.5',
  ],
  /**
   * TWO HANDS CLASPED: an agreement between the clinic and someone outside it.
   * `people` is two people in a process the clinic runs; this is the party on
   * the other side of a deal — the referral partner, and any counterparty the
   * clinic contracts with rather than treats. Referral partners reached for
   * `branch` before this existed, which was External costs' picture then, and
   * the registry is where that answer belongs rather than at either call site.
   */
  handshake: [
    'm11 17 2 2a1 1 0 1 0 3-3',
    'm14 14 2.5 2.5a1 1 0 1 0 3-3l-3.88-3.88a3 3 0 0 0-4.24 0l-.88.88a1 1 0 1 1-3-3l2.81-2.81a5.79 5.79 0 0 1 7.06-.87l.47.28a2 2 0 0 0 1.42.25L21 4',
    'm21 3 1 11h-2',
    'M3 3 2 14l6.5 6.5a1 1 0 1 0 3-3',
    'M3 4h8',
  ],
  /** A clock: a moment something is due, and how far past it the day is. */
  clock: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z', 'M12 7.5V12l3 2'],
  /** A speech bubble: a message a person sent and is waiting on. */
  chat: ['M20 13a2 2 0 0 1-2 2H9l-4 4V6a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2z'],
  /** A handset: the call itself, not the number it was placed to. */
  phone: [
    'M6.5 3h3.1l1.7 4.3-2.2 1.4a12.5 12.5 0 0 0 6.2 6.2l1.4-2.2 4.3 1.7v3.1a2 2 0 0 1-2 2A17 17 0 0 1 4.5 5a2 2 0 0 1 2-2Z',
  ],
  /** A camera: an appointment held over video rather than in a room. */
  video: [
    'M4 6h9a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z',
    'm15 10.5 6-3.5v10l-6-3.5z',
  ],
  /** A bed: admission, the stay itself and the discharge that ends it. */
  bed: [
    'M3 19V7',
    'M3 12h13a5 5 0 0 1 5 5v2H3z',
    'M7.5 11a1.75 1.75 0 1 0 0-3.5 1.75 1.75 0 0 0 0 3.5Z',
  ],
  /** A jigsaw piece: a module fitted into the product rather than part of it.
   *  Also the picture for a module whose own mark this build does not ship. */
  puzzle: ['M5 20V9h5.5a2 2 0 0 1 4 0H19v5a2 2 0 0 1 0 4v2Z'],

  /* Writing marks ---------------------------------------------------------
     The Markdown toolbar's vocabulary. Each is the mark a reader sees in the
     text the button makes, not the name of the syntax behind it. */

  /** A heading: the capital H. */
  heading: ['M6 4v16', 'M18 4v16', 'M6 12h12'],
  /** Bold: the capital B with its two bowls. */
  bold: ['M7 4h7a4 4 0 0 1 0 8H7z', 'M7 12h8a4 4 0 0 1 0 8H7z'],
  /** Italic: the leaning I. */
  italic: ['M10 4h9', 'M5 20h9', 'm15 4-6 16'],
  /** Struck through: an S with the line across it. */
  strikethrough: [
    'M16 6.5c-.7-1.5-2.2-2.5-4-2.5-2.5 0-4.5 1.6-4.5 3.5',
    'M8 17.5c.7 1.5 2.2 2.5 4 2.5 2.5 0 4.5-1.6 4.5-3.5',
    'M4 12h16',
  ],
  /** Code: the angle brackets with the slash between them. Mirrors, because
   *  the brackets open toward the reading direction. */
  code: ['m8 7-5 5 5 5', 'm16 7 5 5-5 5', 'm14 4-4 16'],
  /** A link: two chain rings. */
  link: [
    'M10 14a4 4 0 0 0 5.66 0l3-3a4 4 0 0 0-5.66-5.66l-1.5 1.5',
    'M14 10a4 4 0 0 0-5.66 0l-3 3a4 4 0 0 0 5.66 5.66l1.5-1.5',
  ],
  /** A bulleted list: three lines, a dot before each. Mirrors: the dots stand
   *  at the start of the line. */
  'list-bulleted': ['M9 6h12', 'M9 12h12', 'M9 18h12', 'M4 6h.01', 'M4 12h.01', 'M4 18h.01'],
  /** A numbered list: the same lines with the first numerals before them. */
  'list-numbered': ['M10 6h11', 'M10 12h11', 'M10 18h11', 'M4 4h1.5v4', 'M4 8h3', 'M4 14a1.5 1.5 0 0 1 3 0c0 1-3 2-3 4h3'],
  /** A task list: the same lines with a tick box before each. */
  'list-checked': ['M10 6h11', 'M10 12h11', 'M10 18h11', 'm3 6 1.5 1.5L7.5 4', 'm3 12 1.5 1.5L7.5 10', 'm3 18 1.5 1.5L7.5 16'],
} as const

export type IconName = keyof typeof ICON_PATHS

/**
 * The glyphs that are SPATIAL STATEMENTS rather than pictures of things, and so
 * mirror with the reading direction. Everything absent from this set is a
 * picture and must not flip: a printer is a printer in Hebrew.
 *
 * Declared with the drawing, because the drawing is what knows. A surface
 * cannot see whether the arrow it just rendered points at the end of the line
 * or at a wall, and the one that reached for `transform: scaleX(-1)` in its own
 * stylesheet fixed its own row and left the next surface to rediscover it.
 */
export const DIRECTIONAL: ReadonlySet<IconName> = new Set<IconName>([
  'arrow-left',
  'arrow-out',
  'chevron-left',
  'chevron-right',
  'chevrons-left',
  'chevrons-right',
  'code',
  'exit',
  'fold',
  'folder-tree',
  'list-bulleted',
  'list-checked',
  'list-numbered',
  'panel-end',
  'panel-start',
])
